import React, { Fragment } from 'react';
import { FormSpy } from 'react-final-form';
import { identity } from 'ramda';
import Form from 'js/components/common/Form';
import { Button } from 'js/components/common/Button';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import DateSpanPicker from 'js/components/common/Fields/DateSpanPicker';
import EditableDropDown from 'js/components/common/Fields/EditableDropDown';
import { WarningMessage } from 'js/components/common/Fields/WarningMessage';
import { usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';
import { EDITABLE_DD_MODES } from 'js/components/common/Fields/EditableDropDown/constants';
import { useGetAscendingOrderLimitError } from 'js/components/main/ResponsibleGaming/hooks';

import Hint from '../Hint';
import { saveLimits } from './server';
import { useOnSubmit } from '../hooks';
import { PendingLimits } from '../PendingLimits';
import { LIMIT_TYPES, LIMITS_TYPE, UNLIMITED } from './constants';
import { formatData, parseLimits, parsePendingLimits } from './helpers';
import { useInitialValues, useValidationConfig, useGetPendingLimitText } from './hooks';

import style from './index.scss';

const isPristine = data => Object.keys(data).length === 0;

export default () => {
    const { config, limits, pendingLimits, fetchPortlet } = usePortletSelector(identity);
    const t = usePortletTranslations();
    const initialValues = useInitialValues();
    const validationConfig = useValidationConfig();
    const getPendingLimitText = useGetPendingLimitText();
    const onSubmit = useOnSubmit({
        fetchLimits: fetchPortlet,
        saveLimits,
        isPristine
    });

    const { noLimitsAllowed = true } = config;
    const { monthlyLoginCount, ...loginTimeLimits } = parseLimits(limits, config);
    const parsedPendingLimits = parsePendingLimits(pendingLimits).map(item => ({
        ...item,
        text: getPendingLimitText(item)
    }));
    const getAscendingOrderLimitError = useGetAscendingOrderLimitError({
        fields: Object.keys(loginTimeLimits), exceptions: [0]
    });

    return (
        <Form
            onSubmit={onSubmit}
            formatData={formatData}
            initialValues={initialValues}
            validationConfig={validationConfig}
            render={({ submitting, pristine, submitError, handleSubmit }) => (
                <form
                    className={style.form}
                    onSubmit={event => event.preventDefault()}
                >
                    <section className={style.loginLimits}>
                        <h2 className={style.title}>
                            {t('limitsTitle')}
                        </h2>
                        <FormSpy subscription={{ values: true }}>{({ values }) => {
                            const ascendingOrderError = getAscendingOrderLimitError({ values });
                            const message = t('playtech.mobile.system.tooltip.limits.ascending.warning');

                            if (!ascendingOrderError) {
                                return null;
                            }

                            return <WarningMessage classNames={style} warning={message} />;
                        }}</FormSpy>
                        {submitError && <ErrorMessage error={submitError} animate={true} />}
                        {config.monthlyLoginCount && (
                            <Fragment>
                                <p className={style.description}>
                                    {t('loginAmountDescription')}
                                </p>
                                <div className={style.fieldSet}>
                                    <EditableDropDown
                                        classNames={style}
                                        dataList={[UNLIMITED]}
                                        className={style.control}
                                        name={monthlyLoginCount.name}
                                        label={t(monthlyLoginCount.label)}
                                        placeholder={t(monthlyLoginCount.placeholder)}
                                        displayValuesMap={{ [UNLIMITED]: t('date.unlimited') }}
                                        mode={noLimitsAllowed ? EDITABLE_DD_MODES.ALL : EDITABLE_DD_MODES.TEXT_ONLY}
                                        aria-label={t([monthlyLoginCount.label, monthlyLoginCount.placeholder])}
                                    />
                                </div>
                            </Fragment>
                        )}

                        {(config.dailyLoginTime || config.weeklyLoginTime || config.monthlyLoginTime) && (
                            <Fragment>
                                <p className={style.description}>
                                    {t('timeLimitsDescription')}
                                </p>
                                <div className={style.fieldSet}>
                                    {Object.entries(loginTimeLimits)
                                        .filter(([, { allowed }]) => allowed)
                                        .map(([key, { limitType, name, label, placeholder, resetDate }]) =>
                                            <DateSpanPicker
                                                key={key}
                                                name={name}
                                                label={t(label)}
                                                title={t(placeholder)}
                                                unlimitedValue={UNLIMITED}
                                                allowUnlimited={noLimitsAllowed}
                                                hint={<Hint date={resetDate} />}
                                                classNames={{ control: style.control }}
                                                useDays={limitType !== LIMIT_TYPES.DAILY_LIMIT}
                                                maxDays={limitType === LIMIT_TYPES.WEEKLY_LIMIT ? 7 : undefined}
                                            />)
                                    }
                                </div>
                            </Fragment>
                        )}
                    </section>

                    {parsedPendingLimits.length > 0 && (
                        <section className={style.appliedLimits}>
                            <h2 className={style.title}>{t('pendingLimitsTitle')}</h2>
                            <PendingLimits
                                classNames={style}
                                limitType={LIMITS_TYPE}
                                limits={parsedPendingLimits}
                            />
                        </section>
                    )}
                    <div className={style.formActions}>
                        <Button
                            type="button"
                            onClick={handleSubmit}
                            className={style.submit}
                            disabled={submitting || pristine}
                        >
                            {t('confirmButtonText')}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};
